<template>
  <div>
    <!-- Brinde de promoção SpendAndGet: leitura, sem edição -->
    <template v-if="isSpendAndGetReward">
      <div
        :id="`${id}_reward_${product.id}`"
        class="reward-quantity-display d-inline-flex align-items-center px-2 py-1 rounded"
      >
        <span class="font-weight-bold">{{ getItemQuantity }}x</span>
      </div>
    </template>

    <!-- Modo EDIT: Controles de quantidade (padrão) -->
    <template v-else-if="mode === 'edit'">
      <FormulateInput
        :id="`${id}_pId_${product.id}`"
        v-model="getItemQuantity"
        type="osk-number-with-buttons"
        :name="`${id}_pId_${product.id}`"
        :right-disabled="addButtonDisabled"
        @focus-intercept="onFocusIntercept"
        @onInput="onOskInput"
        @focus="onFocus"
        @blur="onBlur"
        @leftClick="onMinusClick"
        @rightClick="onPlusClick"
      />
    </template>

    <!-- Modo ADD: Botão de adicionar sugestão -->
    <template v-else-if="mode === 'add'">
      <e-button
        :id="`${id}_add_btn`"
        variant="primary"
        size="sm"
        class="atm-btn"
        icon="plus"
        :busy="busy"
        @click="onAddSuggestionClick"
      />
    </template>

    <template v-else-if="mode === 'add-suggestion'">
      <e-button
        :id="`${id}_add_btn`"
        variant="outline-primary"
        size="sm"
        class="atm-btn"
        :text="$t('Adicionar')"
        :busy="busy"
        @click="onAddSuggestionClick"
      />
    </template>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import { debounce } from 'lodash'
import { payBoxRewards, payBoxSale, payBoxUtils, saleItemUtils } from '@/mixins'
import Decimal from 'decimal.js-light'
import EButton from '@/views/components/EButton.vue'

export default {

  components: {
    EButton,
  },

  mixins: [payBoxRewards],
  props: {
    product: {
      type: Object,
      required: true,
    },
    id: {
      type: String,
      default: 'itemInput',
    },
    cartItem: {
      type: Object,
      default: null,
    },
    /** Modo de operação: 'edit' (controles de quantidade) ou 'add' (botão de adicionar) */
    mode: {
      type: String,
      default: 'edit',
      validator: value => ['edit', 'add', 'add-suggestion'].includes(value),
    },
    /** Flag de carregamento para modo 'add' */
    busy: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      quantityAux: 0,
      showLocalQuantity: false,
    }
  },

  computed: {
    ...mapState('pages/pdv', ['modalProductOptionRef']),
    ...mapGetters('pages/pdv/payBox', ['getItems', 'getDictSaleItems']),

    getKeyData() {
      // item.id pode ser kitId ou o skuId
      return {
        kitId: (this.product.kit && this.product.id) || 0,
        promotionId: this.product.promotionId,
        skuId: !this.product.kit && this.product.id,
      }
    },

    addButtonDisabled() {
      let stock = this.product?.stock
      if (this.cartItem) {
        stock = this.cartItem?.skuProduct?.stock
      }

      const itemQuantity = this.getItemQuantity

      stock = saleItemUtils.getStockIfExceedQuantity(stock, itemQuantity)
      if (stock > 0) return true

      if (this.isPromotion && this.cartItem) {
        const { promotionQuantityAvailable, promotionQuantityUnlimited } =
          this.cartItem?.priceRuleSelected?.promotion

        return (
          itemQuantity > 0 &&
          !promotionQuantityUnlimited &&
          itemQuantity >= promotionQuantityAvailable
        )
      }

      return false
    },

    hasTieredPrice() {
      if (this.cartItem) {
        const priceRule = this.cartItem?.priceRuleSelected
        return priceRule.type === payBoxUtils.priceRuleTypes.TIERED_PRICE
      }

      return this.product?.hasTieredPrices
    },

    isPromotion() {
      if (this.cartItem) {
        const priceRule = this.cartItem?.priceRuleSelected
        return priceRule.type === payBoxUtils.priceRuleTypes.PROMOTION
      }

      return this.product?.promotionId
    },

    /**
     * Indica se o cartItem é um brinde de promoção `SpendAndGet`. Quando
     * verdadeiro, o input não permite alterar a quantidade — apenas o botão
     * de remover (no `AtmCartItem`) é exposto ao usuário.
     */
    isSpendAndGetReward() {
      if (!this.cartItem) return false
      if (this.cartItem.isReward) return true

      const priceRule = this.cartItem?.priceRuleSelected
      return priceRule?.type === payBoxUtils.priceRuleTypes.SPEND_AND_GET_REWARD
    },

    getItemQuantity: {
      get() {
        let result = 0
        if (this.showLocalQuantity) {
          result = this.quantityAux
        } else {
          result = this.getQuantity()
        }

        return result
      },
      set() {},
    },
  },

  mounted() {
    this.reloadItemQuantity()
  },

  methods: {
    ...mapActions('pages/pdv/atm', {
      stAddItemFromAtm: 'addItemFromAtm',
      stRemoveItemFromAtm: 'removeItemFromAtm',
      stUpdateItemFromAtm: 'updateItemFromAtm',
    }),
    ...mapActions('pages/pdv/atm/catalog', {
      stFetchProductToCart: 'fetchProductToCart',
    }),

    reloadItemQuantity() {
      this.quantityAux = this.getQuantity()
    },

    getItemCart() {
      const itemFound = this.getItems?.find(
        payBoxSale.methods.productInSaleItemFilter(this.product)
      )
      return itemFound
    },
    getDuplicatedItem() {
      const shouldCheckForDuplicatedItem = !this.cartItem && this.product.promotionId

      if (!shouldCheckForDuplicatedItem) {
        return null
      }

      const saleItemKeyWithoutPromotion = payBoxSale.methods.getSaleProductKey({
        ...this.getKeyData,
        promotionId: null,
      })
      return this.getDictSaleItems[saleItemKeyWithoutPromotion]
    },

    getQuantity() {
      const saleItemKey = payBoxSale.methods.getSaleProductKey(this.getKeyData)
      const baseAmount = this.getDictSaleItems[saleItemKey]?.quantity || 0

      const duplicatedItem = this.getDuplicatedItem()
      const amountInDuplicates = duplicatedItem?.quantity || 0
      const result = new Decimal(baseAmount).add(amountInDuplicates).toNumber()

      if (!this.showLocalQuantity) this.quantityAux = result

      return result
    },

    async onFocusIntercept(focusCallback) {
      const isToShowSuggestionModal = this.modalProductOptionRef.isToShowModal({
        hasTieredPrices: this.hasTieredPrice,
        hasPromotion: this.isPromotion,
      })
      if (!isToShowSuggestionModal) {
        if (focusCallback) focusCallback()
        return false
      }

      document.activeElement.blur()

      try {
        const data = await this.modalProductOptionRef.showModalAsync(
          null,
          {
            quantity: this.getQuantity(),
          },
          () =>
            this.stFetchProductToCart({
              ean: this.product.ean,
              skuId: this.product.id,
              isKit: this.product.kit,
            })
        )

        if (data) {
          this.manageItemDebounce(data.quantity)
        }
      } catch (error) {
        this.modalProductOptionRef.hideModal()
        this.showAtmAlert({ error, icon: 'warning' })
        return true
      }

      return true
    },

    async onOskInput(val) {
      this.quantityAux = val
    },
    onFocus(val) {
      this.showLocalQuantity = true
      this.quantityAux = val || 0
    },
    async onBlur() {
      await this.manageItem(this.quantityAux)
      this.showLocalQuantity = false
    },

    async onMinusClick() {
      if (await this.onFocusIntercept()) return

      if (this.quantityAux - 1 < 0) {
        return
      }

      this.showLocalQuantity = true
      this.quantityAux -= 1

      this.manageItemDebounce(this.quantityAux)
    },
    async onPlusClick() {
      if (await this.onFocusIntercept()) return

      const stock = saleItemUtils.getStockIfExceedQuantity(
        this.product?.stock,
        this.quantityAux + 1
      )
      if (stock > this.quantityAux + 1) return

      this.showLocalQuantity = true
      this.quantityAux += 1

      this.manageItemDebounce(this.quantityAux)
    },

    onRemoveItem() {
      const item = this.getItemCart()
      this.removeItem(item)
    },

    // eslint-disable-next-line func-names
    manageItemDebounce: debounce(async function (quantity) {
      await this.manageItem(quantity)
    }, 500),

    async manageItem(quantity) {
      try {
        const item = this.getItemCart()

        if (!item && quantity === 0) return

        let quantityToSet = quantity
        const stock = saleItemUtils.getStockIfExceedQuantity(this.product?.stock, quantity)
        if (stock) quantityToSet = stock

        if (item && quantityToSet <= 0) {
          await this.removeItem(item)
          return
        }

        if (item && this.getQuantity() > 0) {
          await this.updateItem(item, quantityToSet)
        } else {
          await this.addItem(quantityToSet)
        }
      } catch (error) {
        this.showAtmAlert({ error, icon: 'warning' })
        throw error
      } finally {
        this.showLocalQuantity = false
      }

      this.notifyQuantityChange(quantity)
    },

    // eslint-disable-next-line func-names
    notifyQuantityChange: debounce(function (quantity) {
      if (!this.getItemCart()) return

      const result = saleItemUtils.getQuantityChangeMessageFromSaleItem({
        ...this.getItemCart(),
        quantity,
      })

      if (result?.message) {
        this.showAtmAlert({
          title: result.title,
          text: result.message,
          icon: 'info',
          timer: null,
        })
      }
    }, 100),

    async addItem(quantity) {
      const duplicatedItem = this.getDuplicatedItem()
      if (duplicatedItem) {
        await this.stRemoveItemFromAtm(duplicatedItem)
      }

      await this.stAddItemFromAtm({
        amount: quantity,
        ean: this.product.ean,
        skuId: this.product.id,
        isKit: this.product.kit,
      })

      await this.mxReloadPayBoxRewardApplied()
    },
    async removeItem(item) {
      if (!this.cartItem) {
        const duplicatedItem = this.getDuplicatedItem()
        if (duplicatedItem) {
          await this.stRemoveItemFromAtm(duplicatedItem)
        }
      }

      await this.stRemoveItemFromAtm(item)

      await this.mxReloadPayBoxRewardApplied()
    },
    async updateItem(item, quantity) {
      const duplicatedItem = this.getDuplicatedItem()

      if ((duplicatedItem?.quantity || 0) + item.quantity === quantity) return

      const { priceRuleSelected } = item
      const havePromotion = priceRuleSelected.type === payBoxUtils.priceRuleTypes.PROMOTION
      if (havePromotion && duplicatedItem) {
        const isToRemoveDuplicatedItem = !priceRuleSelected.promotion?.promotionQuantityUnlimited

        if (isToRemoveDuplicatedItem) {
          await this.stRemoveItemFromAtm(duplicatedItem)
        }
      }

      await this.stUpdateItemFromAtm({
        item,
        amount: quantity,
      })

      await this.mxReloadPayBoxRewardApplied()
    },

    /**
     * Manipula o clique no botão de adicionar sugestão (modo 'add')
     * Abre o modal de preço escalonado se necessário e emite evento de adicionar
     */
    async onAddSuggestionClick() {
      try {
        // Verificar se precisa abrir modal de preço escalonado
        const isToShowSuggestionModal = this.modalProductOptionRef.isToShowModal({
          hasTieredPrices: this.hasTieredPrice,
          hasPromotion: this.isPromotion,
        })

        if (isToShowSuggestionModal) {
          // Abrir modal com quantidade padrão de 1
          document.activeElement.blur()

          const data = await this.modalProductOptionRef.showModalAsync(
            null,
            {
              quantity: 1,
            },
            () =>
              this.stFetchProductToCart({
                ean: this.product.ean,
                skuId: this.product.id,
                isKit: this.product.kit,
              })
          )

          // Se usuário confirmou no modal, emitir com a quantidade escolhida
          if (data) {
            const quantity = data.quantity || 1
            this.$emit('add-suggestion', {
              quantity,
              skuId: this.product.id,
              ean: this.product?.ean ?? this.cartItem?.ean,
            })
          }
        } else {
          // Sem modal, emitir direto com quantidade 1
          this.$emit('add-suggestion', {
            quantity: 1,
            skuId: this.product.id,
            ean: this.product?.ean ?? this.cartItem?.ean,
          })
        }
      } catch (error) {
        this.showAtmAlert({ error, icon: 'warning' })
        throw error
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.reward-quantity-display {
  background-color: rgba(40, 199, 111, 0.12);
  color: #28c76f;
  font-size: 0.95rem;
  min-width: 60px;
  justify-content: center;
}
</style>
